import { useState, useRef, useEffect } from 'react';
import { Sport } from '../types';
import { BOOKMAKERS_BY_REGION, REGION_LABELS } from '../bookmakers';
import { PLAYER_PROP_LEAGUES, MAX_PLAYER_PROP_MARKETS } from '../playerPropMarkets';
import BookmakerLogo from './BookmakerLogo';

interface InlineFiltersProps {
  sports: Sport[];
  selectedSports: string[];
  setSelectedSports: (sports: string[]) => void;
  selectedBookmakers: string[];
  setSelectedBookmakers: (bookmakers: string[]) => void;
  selectedGameStatus: string[];
  setSelectedGameStatus: (status: string[]) => void;
  selectedMarkets: string[];
  setSelectedMarkets: (markets: string[]) => void;
  selectedPlayerPropMarkets: string[];
  setSelectedPlayerPropMarkets: (markets: string[]) => void;
  minProfit: number;
  setMinProfit: (profit: number) => void;
  maxProfit: number;
  setMaxProfit: (profit: number) => void;
  userTier?: 'free' | 'gold' | 'platinum' | null;
  onApplyFilters: () => void;
}

export default function InlineFilters({
  sports,
  selectedSports,
  setSelectedSports,
  selectedBookmakers,
  setSelectedBookmakers,
  selectedGameStatus,
  setSelectedGameStatus,
  selectedMarkets,
  setSelectedMarkets,
  selectedPlayerPropMarkets,
  setSelectedPlayerPropMarkets,
  minProfit,
  setMinProfit,
  maxProfit,
  setMaxProfit,
  userTier = 'free',
  onApplyFilters,
}: InlineFiltersProps) {
  const [sportDropdownOpen, setSportDropdownOpen] = useState(false);
  const [bookmakerDropdownOpen, setBookmakerDropdownOpen] = useState(false);
  const [profitDropdownOpen, setProfitDropdownOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const [expandedLeagues, setExpandedLeagues] = useState<string[]>([]);

  const sportDropdownRef = useRef<HTMLDivElement>(null);
  const bookmakerDropdownRef = useRef<HTMLDivElement>(null);
  const profitDropdownRef = useRef<HTMLDivElement>(null);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sportDropdownRef.current && !sportDropdownRef.current.contains(event.target as Node)) {
        setSportDropdownOpen(false);
      }
      if (bookmakerDropdownRef.current && !bookmakerDropdownRef.current.contains(event.target as Node)) {
        setBookmakerDropdownOpen(false);
      }
      if (profitDropdownRef.current && !profitDropdownRef.current.contains(event.target as Node)) {
        setProfitDropdownOpen(false);
      }
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(event.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSportToggle = (sportKey: string) => {
    if (sportKey === 'upcoming') {
      setSelectedSports(['upcoming']);
      return;
    }

    const newSelection = selectedSports.filter(s => s !== 'upcoming');

    if (newSelection.includes(sportKey)) {
      const filtered = newSelection.filter(s => s !== sportKey);
      setSelectedSports(filtered.length > 0 ? filtered : ['upcoming']);
    } else {
      setSelectedSports([...newSelection, sportKey]);
    }
  };

  const handleBookmakerToggle = (bookmakerKey: string) => {
    if (selectedBookmakers.includes(bookmakerKey)) {
      setSelectedBookmakers(selectedBookmakers.filter(b => b !== bookmakerKey));
    } else {
      setSelectedBookmakers([...selectedBookmakers, bookmakerKey]);
    }
  };

  const handleSelectAllInRegion = (regionKey: string) => {
    const regionBookmakers = BOOKMAKERS_BY_REGION[regionKey as keyof typeof BOOKMAKERS_BY_REGION];
    const regionKeys = regionBookmakers.map(b => b.key);
    const allSelected = regionKeys.every(key => selectedBookmakers.includes(key));

    if (allSelected) {
      setSelectedBookmakers(selectedBookmakers.filter(key => !regionKeys.includes(key)));
    } else {
      const newSelection = [...selectedBookmakers];
      regionKeys.forEach(key => {
        if (!newSelection.includes(key)) {
          newSelection.push(key);
        }
      });
      setSelectedBookmakers(newSelection);
    }
  };

  const handleGameStatusToggle = (status: string) => {
    if (selectedGameStatus.includes(status)) {
      const filtered = selectedGameStatus.filter(s => s !== status);
      setSelectedGameStatus(filtered.length > 0 ? filtered : [status]);
    } else {
      setSelectedGameStatus([...selectedGameStatus, status]);
    }
  };

  // Market selection handlers
  const isMoneylineSelected = selectedMarkets.includes('h2h');
  const playerPropCount = selectedPlayerPropMarkets.length;
  const totalMarketsSelected = (isMoneylineSelected ? 1 : 0) + playerPropCount;
  const canAddMore = playerPropCount < MAX_PLAYER_PROP_MARKETS;

  const handleMoneylineToggle = () => {
    if (isMoneylineSelected) {
      if (playerPropCount === 0) {
        return;
      }
      setSelectedMarkets(selectedMarkets.filter(m => m !== 'h2h'));
    } else {
      setSelectedMarkets(['h2h', ...selectedMarkets.filter(m => m !== 'h2h')]);
    }
  };

  const handlePlayerPropToggle = (marketKey: string) => {
    if (selectedPlayerPropMarkets.includes(marketKey)) {
      const filtered = selectedPlayerPropMarkets.filter(m => m !== marketKey);
      setSelectedPlayerPropMarkets(filtered);
      if (filtered.length === 0 && !isMoneylineSelected) {
        setSelectedMarkets(['h2h', ...selectedMarkets.filter(m => m !== 'h2h')]);
      }
    } else if (canAddMore) {
      setSelectedPlayerPropMarkets([...selectedPlayerPropMarkets, marketKey]);
    }
  };

  const handleLeagueExpand = (leagueKey: string) => {
    if (expandedLeagues.includes(leagueKey)) {
      setExpandedLeagues(expandedLeagues.filter(k => k !== leagueKey));
    } else {
      setExpandedLeagues([...expandedLeagues, leagueKey]);
    }
  };

  const getSelectedMarketsForLeague = (leagueKey: string) => {
    const league = PLAYER_PROP_LEAGUES.find(l => l.key === leagueKey);
    if (!league) return 0;
    return league.markets.filter(m => selectedPlayerPropMarkets.includes(m.key)).length;
  };

  const getSportDisplayText = () => {
    if (selectedSports.includes('upcoming')) return 'All Sports';
    if (selectedSports.length === 0) return 'Select Sports';
    if (selectedSports.length === 1) {
      const sport = sports.find(s => s.key === selectedSports[0]);
      return sport?.title || selectedSports[0];
    }
    return `${selectedSports.length} Sports`;
  };

  const getBookmakerDisplayText = () => {
    if (selectedBookmakers.length === 0) return 'All Sportsbooks';
    if (selectedBookmakers.length === 1) {
      const allBookmakers = Object.values(BOOKMAKERS_BY_REGION).flat();
      const bookmaker = allBookmakers.find(b => b.key === selectedBookmakers[0]);
      return bookmaker?.title || selectedBookmakers[0];
    }
    return `${selectedBookmakers.length} Books`;
  };

  const getMarketsDisplayText = () => {
    if (totalMarketsSelected === 0) return 'Select Markets';
    if (totalMarketsSelected === 1 && isMoneylineSelected) return 'Moneyline';
    if (isMoneylineSelected && playerPropCount > 0) {
      return `ML + ${playerPropCount} Prop${playerPropCount === 1 ? '' : 's'}`;
    }
    if (playerPropCount > 0) {
      return `${playerPropCount} Prop${playerPropCount === 1 ? '' : 's'}`;
    }
    return `${totalMarketsSelected} Markets`;
  };

  // Determine effective tier (null means still loading, treat as free for UI)
  const effectiveTier = userTier || 'free';
  const canSelectPlayerProps = effectiveTier === 'platinum';

  const closeAllDropdowns = () => {
    setSportDropdownOpen(false);
    setBookmakerDropdownOpen(false);
    setProfitDropdownOpen(false);
    setMarketsDropdownOpen(false);
  };

  return (
    <div className="inline-filters">
      <div className="inline-filters-row">
        {/* Sport Dropdown */}
        <div className="inline-filter-group" ref={sportDropdownRef}>
          <label className="inline-filter-label">Sport</label>
          <button
            className={`inline-filter-dropdown-btn ${sportDropdownOpen ? 'open' : ''}`}
            onClick={() => {
              closeAllDropdowns();
              setSportDropdownOpen(!sportDropdownOpen);
            }}
          >
            <span>{getSportDisplayText()}</span>
            <svg className="dropdown-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          {sportDropdownOpen && (
            <div className="inline-filter-dropdown">
              <label className="inline-filter-option">
                <input
                  type="checkbox"
                  checked={selectedSports.includes('upcoming')}
                  onChange={() => handleSportToggle('upcoming')}
                />
                <span>All Upcoming Games</span>
              </label>
              <div className="inline-filter-divider"></div>
              {sports.map((sport) => (
                <label key={sport.key} className="inline-filter-option">
                  <input
                    type="checkbox"
                    checked={selectedSports.includes(sport.key)}
                    onChange={() => handleSportToggle(sport.key)}
                  />
                  <span>{sport.title}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Bookmaker Dropdown */}
        <div className="inline-filter-group inline-filter-group-wide" ref={bookmakerDropdownRef}>
          <label className="inline-filter-label">Sportsbooks</label>
          <button
            className={`inline-filter-dropdown-btn ${bookmakerDropdownOpen ? 'open' : ''}`}
            onClick={() => {
              closeAllDropdowns();
              setBookmakerDropdownOpen(!bookmakerDropdownOpen);
            }}
          >
            <span>{getBookmakerDisplayText()}</span>
            <svg className="dropdown-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          {bookmakerDropdownOpen && (
            <div className="inline-filter-dropdown inline-filter-dropdown-wide">
              {selectedBookmakers.length > 0 && (
                <button
                  className="inline-filter-clear-btn"
                  onClick={() => setSelectedBookmakers([])}
                >
                  Clear All ({selectedBookmakers.length})
                </button>
              )}
              {Object.entries(BOOKMAKERS_BY_REGION).map(([regionKey, bookmakers]) => {
                const regionKeys = bookmakers.map(b => b.key);
                const allSelected = regionKeys.every(key => selectedBookmakers.includes(key));

                return (
                  <div key={regionKey} className="inline-filter-region">
                    <div className="inline-filter-region-header">
                      <span className="inline-filter-region-title">
                        {REGION_LABELS[regionKey] || regionKey.toUpperCase()}
                      </span>
                      <button
                        className="inline-filter-select-all"
                        onClick={() => handleSelectAllInRegion(regionKey)}
                      >
                        {allSelected ? 'Clear' : 'All'}
                      </button>
                    </div>
                    <div className="inline-filter-bookmaker-grid">
                      {bookmakers.map((bookmaker) => {
                        const isSelected = selectedBookmakers.includes(bookmaker.key);
                        return (
                          <div
                            key={bookmaker.key}
                            className={`inline-filter-bookmaker ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleBookmakerToggle(bookmaker.key)}
                          >
                            <BookmakerLogo
                              bookmakerKey={bookmaker.key}
                              bookmakerTitle={bookmaker.title}
                            />
                            <span className="inline-filter-bookmaker-name">{bookmaker.title}</span>
                            {isSelected && <span className="inline-filter-check">✓</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Markets Dropdown */}
        <div className="inline-filter-group inline-filter-group-wide" ref={marketsDropdownRef}>
          <label className="inline-filter-label">Markets (Props {playerPropCount}/{MAX_PLAYER_PROP_MARKETS})</label>
          <button
            className={`inline-filter-dropdown-btn ${marketsDropdownOpen ? 'open' : ''}`}
            onClick={() => {
              closeAllDropdowns();
              setMarketsDropdownOpen(!marketsDropdownOpen);
            }}
          >
            <span>{getMarketsDisplayText()}</span>
            <svg className="dropdown-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          {marketsDropdownOpen && (
            <div className="inline-filter-dropdown inline-filter-dropdown-markets">
              {/* Counter */}
              <div className="inline-filter-markets-counter">
                <span>{playerPropCount} of {MAX_PLAYER_PROP_MARKETS} player prop markets selected</span>
                {totalMarketsSelected > 0 && (
                  <button
                    className="inline-filter-clear-btn-small"
                    onClick={() => {
                      setSelectedMarkets(['h2h']);
                      setSelectedPlayerPropMarkets([]);
                    }}
                  >
                    Reset
                  </button>
                )}
              </div>

              {/* Moneyline */}
              <div className="inline-filter-market-section">
                <label className="inline-filter-option inline-filter-option-market">
                  <input
                    type="checkbox"
                    checked={isMoneylineSelected}
                    onChange={handleMoneylineToggle}
                    disabled={isMoneylineSelected && playerPropCount === 0}
                  />
                  <span>Moneyline (H2H)</span>
                </label>
              </div>

              <div className="inline-filter-divider"></div>

              {/* Player Props by League */}
              <div className="inline-filter-market-section">
                <div className="inline-filter-section-header">
                  <span>Player Props</span>
                  {!canSelectPlayerProps && (
                    <span className="inline-filter-platinum-badge">Platinum</span>
                  )}
                </div>

                {canSelectPlayerProps ? (
                  <div className="inline-filter-leagues">
                    {PLAYER_PROP_LEAGUES.map((league) => {
                      const isExpanded = expandedLeagues.includes(league.key);
                      const selectedCount = getSelectedMarketsForLeague(league.key);
                      const standardMarkets = league.markets.filter(m => !m.isAlternate);

                      return (
                        <div key={league.key} className="inline-filter-league">
                          <button
                            className="inline-filter-league-header"
                            onClick={() => handleLeagueExpand(league.key)}
                          >
                            <svg
                              className={`inline-filter-league-chevron ${isExpanded ? 'expanded' : ''}`}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                            <span className="inline-filter-league-name">{league.name}</span>
                            {selectedCount > 0 && (
                              <span className="inline-filter-league-count">{selectedCount}</span>
                            )}
                          </button>
                          {isExpanded && (
                            <div className="inline-filter-league-markets">
                              {standardMarkets.map((market) => {
                                const isSelected = selectedPlayerPropMarkets.includes(market.key);
                                const isDisabled = !isSelected && !canAddMore;

                                return (
                                  <label
                                    key={market.key}
                                    className={`inline-filter-option inline-filter-option-market ${isDisabled ? 'disabled' : ''}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handlePlayerPropToggle(market.key)}
                                      disabled={isDisabled}
                                    />
                                    <span>{market.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="inline-filter-upgrade-notice">
                    <p>Upgrade to Platinum to access player prop markets</p>
                    <a href="/account/billing" className="inline-filter-upgrade-btn">
                      Upgrade Now
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Profit Range Dropdown */}
        <div className="inline-filter-group" ref={profitDropdownRef}>
          <label className="inline-filter-label">Profit %</label>
          <button
            className={`inline-filter-dropdown-btn ${profitDropdownOpen ? 'open' : ''}`}
            onClick={() => {
              closeAllDropdowns();
              setProfitDropdownOpen(!profitDropdownOpen);
            }}
          >
            <span>{minProfit}% - {maxProfit}%</span>
            <svg className="dropdown-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          {profitDropdownOpen && (
            <div className="inline-filter-dropdown inline-filter-dropdown-profit">
              <div className="inline-filter-range">
                <label>Min: {minProfit.toFixed(1)}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={minProfit}
                  onChange={(e) => setMinProfit(parseFloat(e.target.value))}
                />
              </div>
              <div className="inline-filter-range">
                <label>Max: {maxProfit.toFixed(1)}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.1"
                  value={maxProfit}
                  onChange={(e) => setMaxProfit(parseFloat(e.target.value))}
                />
              </div>
            </div>
          )}
        </div>

        {/* Game Status Toggles */}
        <div className="inline-filter-group">
          <label className="inline-filter-label">Status</label>
          <div className="inline-filter-status-toggles">
            <button
              className={`inline-filter-status-btn ${selectedGameStatus.includes('live') ? 'active' : ''} ${effectiveTier === 'free' ? 'disabled' : ''}`}
              onClick={() => {
                if (effectiveTier !== 'free') {
                  handleGameStatusToggle('live');
                }
              }}
              disabled={effectiveTier === 'free'}
              title={effectiveTier === 'free' ? 'Live games require Gold or Platinum' : 'Live games'}
            >
              Live
            </button>
            <button
              className={`inline-filter-status-btn ${selectedGameStatus.includes('upcoming') ? 'active' : ''}`}
              onClick={() => handleGameStatusToggle('upcoming')}
            >
              Upcoming
            </button>
          </div>
        </div>

        {/* Apply Button */}
        <div className="inline-filter-group inline-filter-apply">
          <button className="inline-filter-apply-btn" onClick={onApplyFilters}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="5"></circle>
              <line x1="16" y1="16" x2="21" y2="21"></line>
            </svg>
            Search
          </button>
        </div>
      </div>
    </div>
  );
}
